import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Popover } from 'antd';
import moment from 'moment';
import {
    CheckCircle2,
    Clock,
    ChevronDown,
    Check,
    Package,
    Layers,
    AlertCircle,
    Info,
    X,
    Loader2
} from 'lucide-react';
import fireAjax from '../../../redux/services';
import { getPermissionKey } from '../../../helper/permissions';
import { CONFIG } from '../../../config';
import { dispatchHandler, getTokenData, handleApiChange } from '../../../helper/apiHelperFunctions';
import { useDispatch, useSelector } from 'react-redux';

// Helper function to format date string to clean DD MMM YYYY format
const formatDate = (dateStr) => {
    if (!dateStr || dateStr === '--' || dateStr === 'null' || dateStr === 'undefined') return '--';
    try {
        const m = moment(dateStr);
        if (!m.isValid()) return dateStr;
        // If timestamp includes time part, format nicely as "DD MMM YYYY, HH:mm" or "DD MMM YYYY"
        if (typeof dateStr === 'string' && dateStr.includes(':')) {
            return m.format('DD MMM YYYY, HH:mm');
        }
        return m.format('DD MMM YYYY');
    } catch (e) {
        return dateStr;
    }
};

function TNATimelinePopover({
    designNo = '',
    orderNumber = '',
    groupName = '',
    children,
    placement = 'bottomLeft',
    onStageClick,
    groupList,
    setGroupList
}) {
    const [open, setOpen] = useState(false);
    const [rawResponse, setRawResponse] = useState(null);
    const [activeGroup, setActiveGroup] = useState(groupName || '');
    // const [groupList, setGroupList] = useState([]);

    const tnaGetAllStatusConfigData = useSelector(state => state?.administrationStore?.tnaGetAllStatusConfigData);
    const tnaStatusTimelineData = useSelector(state => state?.digivendStore?.tnaStatusTimelineData);
    const getDropdownData = useSelector(state => state?.generalStore?.getDropdownData);

    const dispatch = useDispatch();

    // useEffect(() => {
    //     if(groupList?.length == 0) {
    //         handleApiChange(dispatch, "getDropdownRequest", { type: "TNA_GROUPS" }, getPermissionKey("purchase", "tnaconfig", "view"));
    //     }
    //     else {
    //         setActiveGroup(groupList?.[0] || "");
    //     }
    // }, []);

    // useEffect(() => {
    //     if (getDropdownData?.isSuccess) {
    //         let data = getDropdownData?.data?.resource || []
    //         if (!data?.includes?.("Others")) {
    //             data?.push?.("Others");
    //         }
    //         setGroupList(data);
    //         setActiveGroup(data?.[0]);
    //     }
    // }, [getDropdownData?.isSuccess])

    const fetchTimelineData = useCallback(async (targetGroup) => {
        let changeGrp = {
            Others: "OTHERS"
        }
        const payload = {
            designNo: designNo || '',
            orderNumber: orderNumber || '',
            groupName: changeGrp?.[targetGroup] || targetGroup || ''
        };
        dispatchHandler(dispatch, "tnaStatusTimelineRequest", payload, getPermissionKey('dashboard', 'tna', 'view'))
    }, [designNo, orderNumber]);

    useEffect(() => {
        if(tnaStatusTimelineData?.isSuccess) {
            setRawResponse(tnaStatusTimelineData?.data?.resource || []);
        }
    }, [tnaStatusTimelineData?.isSuccess])

    // Handle popover open/close
    const handleOpenChange = (newOpen) => {
        setOpen(newOpen);
        if (newOpen) {
            fetchTimelineData(activeGroup || groupName);
        }
    };

    // Handle group tab selection
    const handleTabChange = (gName) => {
        setActiveGroup(gName);
        fetchTimelineData(gName);
    };

    // Transform raw backend response into groups, progress counts, and stages
    const parsedData = useMemo(() => {
        if (!rawResponse) return null;

        const resData = rawResponse?.data || rawResponse?.resource || rawResponse;

        let groups = [];
        let allStages = [];

        if (Array.isArray(resData?.groups)) {
            groups = resData.groups;
            groups.forEach(g => {
                if (Array.isArray(g.stages)) {
                    allStages.push(...g.stages);
                }
            });
        } else {
            const stagesList = Array.isArray(resData)
                ? resData
                : Array.isArray(resData?.resource)
                    ? resData.resource
                    : Array.isArray(resData?.stages)
                        ? resData.stages
                        : [];

            allStages = stagesList;

            const groupMap = {};
            stagesList.forEach((stg) => {
                const gKey = stg.groupName || stg.group_name || stg.group || 'Other';
                if (!groupMap[gKey]) {
                    groupMap[gKey] = [];
                }
                groupMap[gKey].push(stg);
            });

            groups = Object.keys(groupMap).map(gKey => {
                const stages = groupMap[gKey];
                const completed = stages.filter(s => {
                    const trackingStatus = (s.tracking_status || s.trackingStatus || '').toLowerCase();
                    const rawStatus = (s.status || s.ontrackStatus || s.ontrack_status || s.stageStatus || '').toLowerCase();
                    const hasActionDate = !!s.action_date || !!s.actionDate;

                    return trackingStatus.includes('completed') ||
                           rawStatus === 'done' || rawStatus === 'completed' || rawStatus === 'on-track' || rawStatus === 'on track' ||
                           hasActionDate;
                }).length;

                return {
                    groupName: gKey,
                    completedCount: completed,
                    totalCount: stages.length,
                    stages: stages
                };
            });
        }

        const totalCompleted = groups.reduce((acc, g) => acc + (g.completedCount || 0), 0);
        const totalStages = groups.reduce((acc, g) => acc + (g.totalCount || (g.stages ? g.stages.length : 0)), 0);
        const percentage = totalStages > 0 ? Math.round((totalCompleted / totalStages) * 100) : 0;

        return {
            totalCompleted,
            totalStages,
            percentage,
            groups,
            allStages
        };
    }, [rawResponse]);

    // Currently selected group data
    const activeGroupData = useMemo(() => {
        if (!parsedData || !parsedData.groups || parsedData.groups.length === 0) return null;
        if (!activeGroup) {
            return parsedData.groups[0];
        }
        return parsedData.groups.find(g => (g.groupName || '').toLowerCase() === activeGroup.toLowerCase()) || parsedData.groups[0];
    }, [parsedData, activeGroup]);

    // Render individual timeline stage row with SLA summary and formatted dates
    const renderStageItem = (stage, idx) => {
        const trackingStatus = (stage.tracking_status || stage.trackingStatus || '').toLowerCase();
        const rawStatus = (stage.status || stage.ontrackStatus || stage.ontrack_status || stage.stageStatus || '').toLowerCase();
        const hasActionDate = !!stage.action_date || !!stage.actionDate;

        const isDone = trackingStatus.includes('completed') ||
                       rawStatus === 'done' || rawStatus === 'completed' || rawStatus === 'on-track' || rawStatus === 'on track' ||
                       hasActionDate;

        const isDelayed = trackingStatus.includes('delayed') ||
                          rawStatus === 'delayed' ||
                          (stage.delay_days && Number(stage.delay_days) > 0) ||
                          (stage.sla_summary && stage.sla_summary.toLowerCase().includes('overdue')) ||
                          (stage.sla_summary && stage.sla_summary.toLowerCase().includes('delayed'));

        const stageName = stage.status_name || stage.statusName || stage.stageName || stage.name || `Stage ${idx + 1}`;
        const rawPlanDate = stage.due_date || stage.planDate || stage.plan_date || stage.plannedDate || stage.valid_to || '--';
        const rawActDate = stage.action_date || stage.actionDate || (stage.creation_time ? stage.creation_time.split(' ')[0] : null) || stage.actDate || stage.act_date || '--';

        const planDateFormatted = formatDate(rawPlanDate);
        const actDateFormatted = formatDate(rawActDate);

        const slaSummary = stage.sla_summary || stage.slaSummary || null;

        return (
            <div
                key={stage.id || idx}
                onClick={() => onStageClick && onStageClick(stage)}
                className={`flex items-center justify-between py-[10px] px-[8px] transition-colors border-b border-[#F1F5F9] last:border-b-0 hover:bg-[#FAFBFC] rounded-[4px] ${
                    onStageClick ? 'cursor-pointer' : ''
                }`}
            >
                <div className="flex items-center gap-3 min-w-0 flex-1 mr-3">
                    {/* Status Check Icon */}
                    <div className="shrink-0">
                        {isDone ? (
                            <div className="w-[20px] h-[20px] rounded-full bg-[#03B675]/15 flex items-center justify-center text-[#03B675]">
                                <Check size={12} strokeWidth={3} />
                            </div>
                        ) : isDelayed ? (
                            <div className="w-[20px] h-[20px] rounded-full bg-[#FD3757]/15 flex items-center justify-center text-[#FD3757]">
                                <Clock size={12} strokeWidth={2.5} />
                            </div>
                        ) : (
                            <div className="w-[20px] h-[20px] rounded-full bg-[#F0F3F6] border border-[#DAE4EE] flex items-center justify-center text-[#707494]">
                                <div className="w-[6px] h-[6px] rounded-full bg-[#707494]" />
                            </div>
                        )}
                    </div>

                    {/* Stage Name, Dates & SLA Summary */}
                    <div className="min-w-0 flex-1">
                        <span className="text-[12px] font-semibold text-[#111D31] block truncate" title={stageName}>
                            {stageName}
                        </span>
                        <span className="text-[11px] text-[#707494] block mt-0.5">
                            Plan: <span className="font-medium text-[#111D31]">{planDateFormatted}</span>
                            {' | '}
                            Act: <span className="font-medium text-[#111D31]">{actDateFormatted}</span>
                        </span>
                        {slaSummary && (
                            <span className={`text-[10px] font-medium block mt-0.5 ${
                                isDelayed ? 'text-[#FD3757]' : isDone ? 'text-[#03B675]' : 'text-[#707494]'
                            }`}>
                                {slaSummary}
                            </span>
                        )}
                    </div>
                </div>

                {/* Status Badge */}
                <div className="shrink-0">
                    {isDone ? (
                        <span className="px-[8px] py-[2px] bg-[#03B675] text-white text-[10px] font-semibold rounded-[4px] uppercase tracking-wider inline-block">
                            DONE
                        </span>
                    ) : isDelayed ? (
                        <span className="px-[8px] py-[2px] bg-[#FD3757] text-white text-[10px] font-semibold rounded-[4px] uppercase tracking-wider inline-block">
                            OVERDUE
                        </span>
                    ) : (
                        <span className="px-[8px] py-[2px] bg-[#67768E] text-white text-[10px] font-semibold rounded-[4px] uppercase tracking-wider inline-block">
                            ACTION DUE
                        </span>
                    )}
                </div>
            </div>
        );
    };

    const popoverContent = (
        <div className="w-[380px] bg-white rounded-[8px] shadow-[0px_8px_24px_rgba(0,0,0,0.12)] border border-[#DAE4EE] overflow-hidden flex flex-col font-sans select-none">
            {/* Header: Workflow Progress */}
            <div className="p-[14px] border-b border-[#DAE4EE] bg-[#F8F9FC]">
                <div className="flex items-center justify-between">
                    <span className="text-[13px] font-semibold text-[#111D31]">
                        Workflow Progress
                    </span>
                    <span className="text-[11px] font-medium text-[#707494]">
                        {parsedData ? `${parsedData.totalCompleted}/${parsedData.totalStages} completed` : 'Loading...'}
                    </span>
                </div>
                {/* Progress bar */}
                <div className="w-full h-[6px] bg-[#E2E8F0] rounded-full overflow-hidden mt-[6px]">
                    <div
                        className="h-full bg-[#FD3757] rounded-full transition-all duration-500"
                        style={{ width: `${parsedData ? parsedData.percentage : 0}%` }}
                    />
                </div>
            </div>

            {/* Group Pills / Tabs Bar */}
            {parsedData && ((groupList && groupList.length > 0) || (parsedData.groups && parsedData.groups.length > 0)) && (
                <div className="flex items-center gap-2 px-[15px] py-[8px] border-b border-[#DAE4EE] overflow-x-auto bg-white no-scrollbar">
                    {(() => {
                        const displayGroups = (groupList && groupList.length > 0) ? groupList : parsedData.groups;
                        const firstGroupName = typeof displayGroups[0] === 'string' ? displayGroups[0] : (displayGroups[0]?.groupName || displayGroups[0]?.name || '');

                        return displayGroups.map((group) => {
                            const gName = typeof group === 'string' ? group : (group?.groupName || group?.name || 'Other');
                            const matchedParsedGroup = parsedData?.groups?.find(g => (g?.groupName || '').toLowerCase() === gName.toLowerCase());
                            const completedCount = matchedParsedGroup ? matchedParsedGroup.completedCount : 0;
                            const totalCount = matchedParsedGroup ? matchedParsedGroup.totalCount : 0;
                            const isSelected = (activeGroup || firstGroupName || '').toLowerCase() === gName.toLowerCase();
                            const isGroupDone = completedCount === totalCount && totalCount > 0;

                            return (
                                <button
                                    key={gName}
                                    onClick={() => handleTabChange(gName)}
                                    className={`flex items-center gap-1.5 px-[10px] py-[4px] rounded-[4px] text-[11px] font-semibold transition-all shrink-0 border ${
                                        isSelected
                                            ? 'bg-[#FFF0F2] text-[#FD3757] border-[#FD3757]/30 shadow-2xs'
                                            : 'bg-[#F8F9FC] text-[#67768E] border-[#DAE4EE] hover:bg-[#F3F4F9]'
                                    }`}
                                >
                                    <span>{gName}</span>
                                    {matchedParsedGroup && (
                                        <span
                                            className={`px-[6px] py-[1px] rounded-[3px] text-[10px] font-bold ${
                                                isGroupDone
                                                    ? 'bg-[#03B675] text-white'
                                                    : isSelected
                                                        ? 'bg-[#FD3757] text-white'
                                                        : 'bg-[#E2E8F0] text-[#475569]'
                                            }`}
                                        >
                                            {completedCount}/{totalCount}
                                        </span>
                                    )}
                                </button>
                            );
                        });
                    })()}
                </div>
            )}

            {/* Stages Timeline Body */}
            <div className="px-[12px] py-[6px] max-h-[280px] overflow-y-auto scrollbar-thin">
                {tnaStatusTimelineData?.isLoading ? (
                    <div className="py-8 flex flex-col items-center justify-center text-gray-400 gap-2">
                        <Loader2 className="w-[20px] h-[20px] animate-spin text-[#FD3757]" />
                        <span className="text-[12px] font-medium text-[#707494]">Fetching T&A status...</span>
                    </div>
                ) : activeGroupData && activeGroupData.stages && activeGroupData.stages.length > 0 ? (
                    activeGroupData.stages.map((stg, idx) => renderStageItem(stg, idx))
                ) : (
                    <div className="py-8 text-center text-[12px] text-[#707494] font-medium">
                        No stages found for this group.
                    </div>
                )}
            </div>

            {/* Footer Instruction */}
            {/* <div className="px-[15px] py-[8px] bg-[#F8F9FC] border-t border-[#DAE4EE] text-[10px] text-[#707494] font-medium text-center">
                Click on incomplete steps to mark as done
            </div> */}
        </div>
    );

    return (
        <Popover
            content={popoverContent}
            trigger={['click']}
            open={open}
            onOpenChange={handleOpenChange}
            placement={placement}
            overlayClassName="tna-timeline-popover"
            destroyTooltipOnHide
        >
            {children || (
                <button className="inline-flex items-center gap-1.5 px-[10px] py-[4px] rounded-[4px] bg-white hover:bg-[#F8F9FC] text-[11px] font-medium text-[#111D31] transition-colors border border-[#DAE4EE]">
                    <span>T&A Timeline</span>
                    <ChevronDown size={14} className="text-[#707494]" />
                </button>
            )}
        </Popover>
    );
}

export default TNATimelinePopover;
